// AF-3.19 P1 — Creator chain: Ideas. Real route (AF-3.4), real field
// names verified against creative_ideation.py's document construction
// (idea_type, ideation_mode, status, generation_status, title, hook,
// core_message, causality, governance_status).

import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { BrandContextBar } from '@/components/brand/BrandContextBar';
import { StatusBadge, GovernanceBadge } from '@/components/brand/Badges';
import { EmptyState, ErrorState, LoadingState } from '@/components/brand/StateViews';
import { EvidenceDrawer } from '@/components/brand/EvidenceDrawer';
import { useBrandContext } from '@/hooks/useBrandContext';
import { creativeIdeaAPI } from '@/lib/intelligenceApi';
import { CaretRight } from '@phosphor-icons/react';

// AF-3.20 — provenance IDs verified against creative_ideation.py's own
// document construction this session. No "evidence" array exists on the
// idea document itself (unlike strategy/review) -- passing [] renders
// EvidenceDrawer's own real "No evidence recorded" empty state rather
// than inventing evidence text.
function ideaProvenance(idea) {
  return {
    source_strategy_ids: idea.source_strategy_ids,
    source_opportunity_ids: idea.source_opportunity_ids,
    source_recommendation_ids: idea.source_recommendation_ids,
    source_action_plan_ids: idea.source_action_plan_ids,
    source_diagnosis_ids: idea.source_diagnosis_ids,
    source_signal_ids: idea.source_signal_ids,
    source_learning_ids: idea.source_learning_ids,
  };
}

// AF-3.19 Phase 7 — minimal Strategy -> Ideas -> Review -> Decisions ->
// Production -> Distribution stepper, shared shape with the other Creator
// chain pages, so this page's position in the sequence is obvious at a
// glance.
const CHAIN_STAGES = ['Strategy', 'Ideas', 'Review', 'Decisions', 'Production', 'Distribution'];

function ChainProgress({ current }) {
  return (
    <div data-testid="creator-chain-progress" className="flex items-center gap-1.5 flex-wrap mb-3">
      {CHAIN_STAGES.map((stage, i) => (
        <span key={stage} className="flex items-center gap-1.5">
          <span className={`text-[10px] font-mono uppercase ${stage === current ? 'text-[var(--accent)] font-medium' : 'text-[var(--text-tertiary)]'}`}>{stage}</span>
          {i < CHAIN_STAGES.length - 1 && <CaretRight size={10} className="text-[var(--text-tertiary)]" />}
        </span>
      ))}
    </div>
  );
}

export default function CreatorIdeasPage() {
  const ctx = useBrandContext();
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [evidenceFor, setEvidenceFor] = useState(null);

  const load = useCallback(() => {
    if (!ctx.ready) return;
    setLoading(true); setError(null);
    creativeIdeaAPI.list(ctx.workspaceId, { entity_type: ctx.entityType, entity_id: ctx.entityId })
      .then((r) => setIdeas(r.data?.ideas || r.data || []))
      .catch((e) => setError(e))
      .finally(() => setLoading(false));
  }, [ctx.ready, ctx.workspaceId, ctx.entityType, ctx.entityId]);

  useEffect(() => { load(); }, [load]);

  return (
    <DashboardLayout>
      <div data-testid="creator-ideas-page" className="max-w-5xl mx-auto">
        <div className="mb-4">
          <h1 className="font-display text-[20px] font-bold text-[var(--text-primary)] mb-1">Creative Ideas</h1>
          <p className="text-[12px] text-[var(--text-secondary)]">Concrete idea variants generated from an approved strategy.</p>
        </div>

        <ChainProgress current="Ideas" />

        <BrandContextBar ctx={ctx} />

        {!ctx.ready && !ctx.loadingClients && <EmptyState title="Select a workspace and entity" description="Choose a brand workspace and entity above to view its ideas." />}
        {loading && <LoadingState />}
        {error && <ErrorState error={error} onRetry={load} />}

        {!loading && !error && ctx.ready && ideas.length === 0 && (
          <EmptyState title="No ideas generated yet" description="Ideas are generated from an approved creative strategy — none exists yet for this entity." />
        )}

        {!loading && !error && ideas.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="creator-ideas-list">
            {ideas.map((idea) => (
              <div key={idea.id} className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-card)]" data-testid="creator-idea-row">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <p className="text-[9px] font-mono uppercase text-[var(--text-tertiary)]">{idea.idea_type?.replace(/_/g, ' ') || 'Idea'}</p>
                  <StatusBadge value={idea.status} />
                </div>
                <p className="text-[13px] text-[var(--text-primary)] font-semibold mb-1">{idea.title || 'Untitled — generation in progress'}</p>
                {idea.hook && <p className="text-[11px] text-[var(--text-secondary)] mb-1">Hook: {idea.hook}</p>}
                {idea.core_message && <p className="text-[11px] text-[var(--text-secondary)] mb-2">{idea.core_message}</p>}
                <div className="flex items-center gap-2 flex-wrap">
                  <StatusBadge value={idea.generation_status} />
                  {idea.governance_status && <GovernanceBadge value={idea.governance_status} />}
                </div>
                {idea.causality && <p className="text-[10px] text-[var(--text-tertiary)] mt-2 italic">{idea.causality}</p>}
                <button data-testid="creator-idea-evidence-trigger" className="mt-2 text-[11px] text-[var(--accent)]" onClick={() => setEvidenceFor(idea)}>
                  Why this idea? →
                </button>
              </div>
            ))}
          </div>
        )}

        {evidenceFor && (
          <EvidenceDrawer
            open={!!evidenceFor}
            onOpenChange={(open) => !open && setEvidenceFor(null)}
            title="Why Martian generated this idea"
            evidence={[]}
            provenance={ideaProvenance(evidenceFor)}
            causality={evidenceFor.causality}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
